import {AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MessageService} from 'primeng/api';
import {SelectButtonModule} from 'primeng/selectbutton';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {defer, Observable, Subject, takeUntil} from 'rxjs';
import {concatMap, finalize} from 'rxjs/operators';
import {AccountService} from '../../../services/account.service';
import {CoordsService} from '../../../services/coords.service';
import {DecoderService} from '../../../services/decoder.service';
import {DeviceProfileService} from '../../../services/device-profile.service';
import {DeviceService} from '../../../services/device.service';
import {ButtonIconComponent} from '../../../shared/components/button-icon/button-icon.component';
import {DialogBase} from '../../../shared/components/dialogs/dialog-base/dialog-base';
import {
  DialogMapCoordsSelector
} from '../../../shared/components/dialogs/dialog-map-coords-selector/dialog-map-coords-selector';
import {DropdownSelector} from '../../../shared/components/dropdown-selector/dropdown-selector';
import {ImageEditGallery} from '../../../shared/components/image-edit-gallery/image-edit-gallery';
import {InputDateComponent} from '../../../shared/components/input-date/input-date.component';
import {InputSelectButton} from '../../../shared/components/input-select-button/input-select-button';
import {InputSelectComponent} from '../../../shared/components/input-select/input-select';
import {InputSubmitComponent} from '../../../shared/components/input-submit/input-submit.component';
import {InputTextComponent} from '../../../shared/components/input-text/input-text.component';
import {InputTextarea} from '../../../shared/components/input-textarea/input-textarea';
import {TranslateSafePipe} from '../../../shared/pipes/translation.pipe';
import {dateToString, formatTimeAgoTranslation} from '../../../shared/utils/date.util';
import {AccountRole} from '../../../types/dtos/account.dto';
import {DecoderDto} from '../../../types/dtos/decoder.dto';
import {
  CoordinatesDto,
  DeviceDto,
  DeviceImageDto,
  DeviceUpdateDTO,
  DeviceUpdateImageDTO,
  DistrictResponseDto,
  InstallationRotationType,
  PipeType,
} from '../../../types/dtos/device.dto';
import {DeviceProfileDto} from '../../../types/dtos/deviceProfile.dto';
import {ImageDto} from '../../../types/dtos/image.dto';
import {
  InputDateInterface,
  InputDropdownSelectorInterface,
  InputImageGalleryInterface,
  InputSelectButtonInterface,
  InputSelectInterface,
  InputSubmitInterface,
  InputTextAreaInterface,
  InputTextInterface,
} from '../../../types/interfaces/input.interface';
import {DeviceEditAlarmRule} from './device-edit-alarm-rule/device-edit-alarm-rule';
import {TranslateService} from '@ngx-translate/core';
import {LoggingActionEnum, LoggingTargetEnum} from '../../../types/enums/logging.enum';
import {LogService} from '../../../services/log.service';

@Component({
  selector: 'app-dialog-edit-device',
  imports: [
    ReactiveFormsModule,
    SelectButtonModule,
    InputTextComponent,
    InputSubmitComponent,
    InputTextarea,
    ButtonIconComponent,
    DialogBase,
    DialogMapCoordsSelector,
    InputSelectComponent,
    InputSelectButton,
    DropdownSelector,
    InputDateComponent,
    ImageEditGallery,
    Tab,
    TabList,
    Tabs,
    TabPanel,
    TabPanels,
    DeviceEditAlarmRule,
    TranslateSafePipe,
  ],
  templateUrl: './dialog-edit-device.html',
  styleUrl: './dialog-edit-device.css',
})
export class DialogEditDevice implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('dialogMapCoordsSelector', {static: true})
  public mapCoordsSelectorDialog!: DialogMapCoordsSelector;

  @ViewChild('imageGallery', {read: ElementRef})
  public imageGalleryElement?: ElementRef<HTMLElement>;

  public isSelectCoords: boolean = false;

  public deviceForm: FormGroup = new FormGroup({
    serial_number: new FormControl(),
    device_eui: new FormControl({
      disabled: true,
      value: '',
    }),
    last_sigi_send_time: new FormControl({
      disabled: true,
      value: '',
    }),
    decoder: new FormControl(),
    device_profile: new FormControl(),
    name: new FormControl(),
    description: new FormControl(),
    address: new FormControl(),
    latitude: new FormControl(),
    longitude: new FormControl(),
    installed_at: new FormControl(),
    installation_district: new FormControl(),
    comments: new FormControl(),
    pipeSize: new FormControl(),
    pipeDn: new FormControl(),
    rotation: new FormControl(),
    pipeType: new FormControl(),
    locationRoom: new FormControl(),
    images: new FormControl<{
      currentImages: ImageDto[],
      newImages: File[];
      deletedImageIds: number[];
    }>({currentImages: [], newImages: [], deletedImageIds: []}),
  });

  public serialNumberConfig: InputTextInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.input.serielNumber.label',
    id: 'serial_number',
    type: 'text',
    iconLeft: 'devices',
  };

  public deviceEUIConfig: InputTextInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.input.deviceEui.label',
    id: 'device_eui',
    readonly: true,
    type: 'text',
    iconLeft: 'code',
  };

  public lastSigiSendTimeConfig: InputTextInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.input.last_sig_send.label',
    id: 'last_sigi_send_time',
    readonly: true,
    type: 'text',
    iconLeft: 'history',
  };

  public deviceProfileConfig: InputSelectInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.fields.deviceProfile',
    id: 'device_profile',
    iconLeft: 'code',
    showClear: true,
    defaultOption: {
      isTranslated: true,
      label: 'common.fields.no-profile-selected',
      value: '-1',
    },
    options: [],
  };

  public deviceDecoderConfig: InputSelectInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.input.decoder.label',
    id: 'decoder',
    iconLeft: 'code',
    showClear: true,
    defaultOption: {
      isTranslated: true,
      label: 'common.input.values.no-decoder',
      value: '-1',
    },
    infoText: 'common.input.decoder.info',
    options: [],
  };

  public addressConfig: InputTextInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.input.address.label',
    id: 'address',
    type: 'text',
    iconLeft: 'location_on',
  };

  public latitudeConfig: InputTextInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.input.latitude.label',
    id: 'latitude',
    type: 'text',
    iconLeft: 'my_location',
  };

  public longitudeConfig: InputTextInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.input.longitude.label',
    id: 'longitude',
    type: 'text',
    iconLeft: 'my_location',
  };

  public installationDateConfig: InputDateInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.input.installed_at.label',
    id: 'installed_at',
    selectionMode: 'single',
    iconLeft: 'event',
    showTime: true,
  };

  public installationDistrictConfig: InputDropdownSelectorInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.input.installation_district.label',
    id: 'installation_district',
    suggestions: [],
    infoText: 'common.input.installation_district.info',
  };

  public commentsConfig: InputTextAreaInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.input.comments.label',
    id: 'comments',
    iconLeft: 'comment',
    infoText: 'common.input.comments.info',
  };

  public imageConfig: InputImageGalleryInterface = {
    formGroup: this.deviceForm,
    id: 'images',
    type: 'file',
    multiple: true,
    canDelete: true,
  };

  public submitConfig: InputSubmitInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.actions.save',
  };

  public updateMeasurementConfig: InputSubmitInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.actions.update-measurements',
  };

  public deviceSizeSelectConfig: InputSelectButtonInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.input.pipeSize.label',
    id: 'pipeSize',
    iconLeft: 'straighten',
    options: [
      {
        label: '1/2"',
        value: '1/2',
      },
      {
        label: '3/4"',
        value: '3/4',
      },
      {
        label: '1"',
        value: '1',
      },
      {
        label: '1 1/4"',
        value: '1 1/4',
      },
      {
        label: '1 1/2"',
        value: '1 1/2',
      },
      {
        label: '2"',
        value: '2',
      },
    ],
  };

  public deviceRotationSelectConfig: InputSelectButtonInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.input.rotation.label',
    iconLeft: 'screen_rotation_up',
    id: 'rotation',
    options: [
      {
        isTranslated: true,
        label: 'common.input.rotation.values.horizontal',
        value: 'horizontal',
      },
      {
        isTranslated: true,
        label: 'common.input.rotation.values.vertical',
        value: 'vertical',
      },
    ],
  };

  public deviceDNSelectConfig: InputSelectButtonInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.input.pipeDn.label',
    id: 'pipeDn',
    iconLeft: 'open_in_full',
    options: [
      {
        label: '50',
        value: '50',
      },
      {
        label: '65',
        value: '65',
      },
      {
        label: '80',
        value: '80',
      },
      {
        label: '100',
        value: '100',
      },
      {
        label: '125',
        value: '125',
      },
      {
        label: '150',
        value: '150',
      },
      {
        label: '200',
        value: '200',
      },
    ],
  };

  public devicePipeTypeDropdownConfig: InputSelectInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.input.pipeType.label',
    id: 'pipeType',
    showClear: true,
    options: [
      {
        label: 'PE',
        value: 'PE',
      },
      {
        label: 'Soccrex',
        value: 'Soccrex',
      },
      {
        label: 'Guss',
        value: 'Guss',
      },
      {
        label: 'Galvan',
        value: 'Galvan',
      },
    ],
  };

  public deviceRoomDropdownConfig: InputDropdownSelectorInterface = {
    formGroup: this.deviceForm,
    isTranslated: true,
    label: 'common.input.locationRoom.label',
    id: 'locationRoom',
    showClear: true,
    suggestions: [
      'Garage',
      'Keller',
      'Bad-WC',
      'Wohnung',
      'Schacht',
    ],
  };

  public loading: boolean = false;
  public requestCoords: boolean = false;
  protected readonly AccountRole = AccountRole;
  private _updateDevice: DeviceUpdateDTO = {};
  private _updateDeviceImages: DeviceUpdateImageDTO = {
    newImages: [],
    deleteIds: [],
  };
  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private readonly _deviceProfileService: DeviceProfileService,
    private readonly _deviceDecoder: DecoderService,
    private readonly _deviceService: DeviceService,
    private readonly _messageService: MessageService,
    private readonly _coordsService: CoordsService,
    private readonly _accountService: AccountService,
    private readonly _translateService: TranslateService,
    private readonly _logService: LogService,
  ) {
  }

  private _device: DeviceDto | null = null;

  public get device(): DeviceDto | null {
    return this._device;
  }

  @Input()
  public set device(value: DeviceDto | null) {
    this._device = value;
    this.deviceForm.reset();

    if (!this._device) return;

    this.loading = true;

    this._deviceService.getDeviceDistricts()
      .subscribe((districts: DistrictResponseDto) => {
        this.installationDistrictConfig.suggestions = districts.data ?? [];
      });

    this._fetchDevice().then();
  }

  ngAfterViewInit(): void {
    this._logService.sendLog({
      target: LoggingTargetEnum.DEVICE,
      action: LoggingActionEnum.VIEW,
      description: 'Schaut Gerät im edit-Modus an',
      target_id: this.device?.id,
    });
  }

  public ngOnInit(): void {
    this._deviceDecoder.decoders$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: DecoderDto[]) => {
        this.deviceDecoderConfig.options = data.map((decoder: DecoderDto) => ({
          label: decoder.name,
          value: decoder.id.toString(),
        }));
      });

    this._deviceProfileService.devicesProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: DeviceProfileDto[]) => {
        this.deviceProfileConfig.options = data.map((profile: DeviceProfileDto) => ({
          label: profile.name,
          value: profile.id.toString(),
        }));
      });

    this._deviceService.devices$
      .pipe(takeUntil(this.destroy$))
      .subscribe((devices: DeviceDto[]) => {
        if (!this._device) {
          return;
        }

        const updatedDevice: DeviceDto | undefined =
          devices.find((device: DeviceDto) => device.id === this._device!.id);

        if (!updatedDevice) {
          return;
        }

        this._device = updatedDevice;
        this._fillForm();
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public hasRole(role: AccountRole): boolean {
    return this._accountService.getCurrentRole() === role;
  }

  public toggleSelectCoordsDialog(): void {
    this.isSelectCoords = !this.isSelectCoords;
  }

  public readonly closeSelectCoordsDialog = (): void => {
    this.isSelectCoords = false;
  };

  public openCoordsSelectionMap(): void {
    if (!this.device) {
      return;
    }

    this.isSelectCoords = true;

    queueMicrotask(() => {
      const lat = this.deviceForm.get('latitude')?.value ?? this.device?.position?.latitude;
      const lng = this.deviceForm.get('longitude')?.value ?? this.device?.position?.longitude;
      const hasCoords: boolean = lat && lng;

      this.mapCoordsSelectorDialog?.setCoords(
        hasCoords
          ? {
            latitude: Number(lat),
            longitude: Number(lng),
          }
          : null,
      );
    });
  }

  public async onCoordsSelected(coords: CoordinatesDto | null): Promise<void> {
    if (!coords) {
      return;
    }

    this.deviceForm.patchValue({
      latitude: coords.latitude,
      longitude: coords.longitude,
    });

    this.closeSelectCoordsDialog();
    this.requestCoords = true;

    const streetName: string = await this._coordsService.streetNameFromCoords(coords);

    this.deviceForm.patchValue({
      address: streetName,
    });

    this.requestCoords = false;
  }

  public updateMeasurements(): void {
    if (!this.device || this.loading) {
      return;
    }

    this._deviceService.updateMeasurement(this.device.id!)
      .subscribe({
        next: () => {
          this._messageService.add({
            severity: 'success',
            summary: this._translateService.instant('common.message.summary.measurements-updated'),
            detail: this._translateService.instant('common.messages.success.measurements-updated'),
            life: 4000,
          });
        },

      });
  }

  public submitOnClick(): void {
    if (!this.device || this.loading) {
      return;
    }

    const hasDeviceChanges: boolean = this._validateFillForm();
    const hasImageChanges: boolean = this._validateImages();
    const deviceId: number = this.device.id;

    if (!hasDeviceChanges && !hasImageChanges) {
      this._messageService.add({
        severity: 'info',
        summary: this._translateService.instant('common.title.info.no-changes'),
        detail: this._translateService.instant('common.messages.info.no-changes'),
        life: 4000,
      });
      return;
    }

    this.loading = true;

    this._saveDeviceChanges(deviceId, hasDeviceChanges, hasImageChanges)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: () => {
          let message: string = this._translateService.instant('common.messages.success.update-images');
          if (hasDeviceChanges && hasImageChanges) {
            message = this._translateService.instant('common.messages.success.update-images-and-device');
          }

          if (hasDeviceChanges) {
            message = this._translateService.instant('common.messages.success.update-device');
          }

          this._handleSaveSuccess(message);
          this._fetchDevice().then();
        },
        error: (error: unknown) => {
          this._handleSaveError(error);
        },
      });
  }

  private _saveDeviceChanges(
    deviceId: number,
    hasDeviceChanges: boolean,
    hasImageChanges: boolean,
  ): Observable<unknown> {
    return defer(() => {
      if (hasDeviceChanges && hasImageChanges) {
        return this._deviceService.updateDevice(deviceId, this._updateDevice)
          .pipe(
            concatMap(() => this._deviceService.uploadDeviceImage(deviceId, this._updateDeviceImages)),
          );
      }

      if (hasDeviceChanges) {
        return this._deviceService.updateDevice(deviceId, this._updateDevice);
      }

      return this._deviceService.uploadDeviceImage(deviceId, this._updateDeviceImages);
    });
  }

  private _fillForm(): void {
    if (!this._device) {
      return;
    }

    this.deviceForm.reset();

    this.deviceForm.patchValue({
      serial_number: this._device.serial_number,
      device_eui: this._device.device_eui,
      last_measurement_received_time: this._device.last_measurement_received_time
        ? dateToString(this._device.last_measurement_received_time)
        : 'N/A',
      last_sigi_send_time: this._device.last_sigi_send_time
        ? formatTimeAgoTranslation(this._translateService, this._device.last_sigi_send_time)
        : 'N/A',
      created_at: this._device.created_at
        ? dateToString(this._device.created_at)
        : 'N/A',
      updated_at: this._device.updated_at
        ? dateToString(this._device.updated_at)
        : 'N/A',
      address: this._device.position?.address,
      latitude: this._device.position?.latitude,
      longitude: this._device.position?.longitude,
      installed_at: this._device.position?.installed_at ? new Date(this._device.position?.installed_at) : new Date(),
      installation_district: this._device.installation_district,
      comments: this._device.comments,
      device_profile: this._device.device_profile_id?.toString(),
      decoder: this._device.decoder_id?.toString(),
      locationRoom: this._device.metadata?.locationRoom,
      pipeSize: this._device.metadata?.pipeSize,
      pipeType: this._device.metadata?.pipeType,
      rotation: this._device.metadata?.rotation,
      pipeDn: this._device.metadata?.pipeDn,
      images: {
        currentImages: (this._device.images ?? []).map((image: DeviceImageDto) => ({
          id: image.id,
          url: image.path,
        })),
        newImages: [],
        deletedImageIds: [],
      },
    });
  }

  private async _fetchDevice(): Promise<void> {
    if (!this._device) {
      return;
    }

    this.loading = true;

    const fullDevice: DeviceDto | null = await this._deviceService.getById(this._device.id);

    if (!fullDevice) {
      console.error('Error fetching device');
      this.loading = false;
      return;
    }

    this._device = fullDevice;
    this._fillForm();
    this.loading = false;
  }

  private _validateImages(): boolean {
    if (!this._device || !this._device.is_full) {
      return false;
    }

    this._updateDeviceImages = {
      newImages: [],
      deleteIds: [],
    };

    const imageControl = this.deviceForm?.get('images');
    if (!imageControl) return false;

    const value = imageControl.value;
    if (!value) return false;

    this._updateDeviceImages.newImages = value.newImages;
    this._updateDeviceImages.deleteIds = value.deletedImageIds;

    return this._updateDeviceImages.newImages.length > 0 || this._updateDeviceImages.deleteIds.length > 0;
  }

  private _validateFillForm(): boolean {
    if (!this._device || !this._device.is_full) {
      return false;
    }

    this._updateDevice = {};

    const serialNumber: string = this.deviceForm.get('serial_number')?.value;

    if (serialNumber !== this._device.serial_number) {
      if (serialNumber.length > 14) {
        this._messageService.add({
          severity: 'error',
          summary: this._translateService.instant('common.title.error'),
          detail: this._translateService.instant('common.messages.error.serial-number-limit'),
          life: 5000,
        });
        return false;
      }

      this._updateDevice.serial_number = serialNumber;
    }

    const deviceProfileId: number = Number(this.deviceForm.get('device_profile')?.value ?? '-1');

    if (!(deviceProfileId === -1 && !this._device.device_profile_id)) {
      if (deviceProfileId !== this._device.device_profile_id) {
        this._updateDevice.device_profile_id = deviceProfileId;
      }
    }

    const decoderId: number = Number(this.deviceForm.get('decoder')?.value ?? '-1');

    if (!(decoderId === -1 && !this._device.decoder_id)) {
      if (decoderId !== this._device.decoder_id) {
        this._updateDevice.decoder_id = decoderId;
      }
    }

    const address: string = this.deviceForm.get('address')?.value;
    const latitude: number = parseFloat(this.deviceForm.get('latitude')?.value);
    const longitude: number = parseFloat(this.deviceForm.get('longitude')?.value);
    const installedAt: string = new Date(this.deviceForm.get('installed_at')?.value).toString();

    if (
      (address && !Number.isNaN(latitude) && !Number.isNaN(longitude))
      && (
        !this._device.position
        || address !== this._device.position.address
        || latitude !== this._device.position.latitude
        || longitude !== this._device.position.longitude
        || installedAt !== this._device.position.installed_at
      )
    ) {
      if (!address || Number.isNaN(latitude) || Number.isNaN(longitude)) {
        this._messageService.add({
          severity: 'warn',
          summary: this._translateService.instant('common.enum.alarm-severity.warning'),
          detail: this._translateService.instant('common.messages.warn.fill-every-field'),
          life: 5000,
        });
        return false;
      }

      this._updateDevice.position = {
        id: this._device.position?.id,
        address: this.deviceForm.get('address')?.value,
        latitude: parseFloat(
          this.deviceForm.get('latitude')
            ?.value
            .toString()
            .replace(',', '.'),
        ),
        longitude: parseFloat(
          this.deviceForm.get('longitude')
            ?.value
            .toString()
            .replace(',', '.'),
        ),
        installed_at: installedAt,
      };
    }

    const comment: string = this.deviceForm.get('comments')?.value;

    if (comment || this._device.comments) {
      if (comment !== this._device.comments) {
        this._updateDevice.comments = comment;
      }
    }

    const pipeSize: string = this.deviceForm.get('pipeSize')?.value;
    const pipeDn: number = Number(this.deviceForm.get('pipeDn')?.value);
    const rotation: string = this.deviceForm.get('rotation')?.value;
    const pipeType: string = this.deviceForm.get('pipeType')?.value;
    const locationRoom: string = this.deviceForm.get('locationRoom')?.value;

    this._updateDevice.metadata = this._updateDevice.metadata ?? {};

    if (pipeSize != this._device.metadata?.pipeSize) {
      this._updateDevice.metadata.pipeSize = pipeSize;
    }

    if (pipeDn != this._device.metadata?.pipeDn) {
      this._updateDevice.metadata.pipeDn = pipeDn;
    }

    if (rotation != this._device.metadata?.rotation) {
      this._updateDevice.metadata.rotation = rotation as InstallationRotationType;
    }

    if (pipeType != this._device.metadata?.pipeType) {
      this._updateDevice.metadata.pipeType = pipeType as PipeType;
    }

    if (locationRoom != this._device.metadata?.locationRoom) {
      this._updateDevice.metadata.locationRoom = locationRoom;
    }

    const installationDistrict: string = this.deviceForm.get('installation_district')?.value;
    if (installationDistrict != this._device.installation_district) {
      this._updateDevice.installation_district = installationDistrict ?? '';
    }

    if (Object.keys(this._updateDevice.metadata).length === 0) {
      delete this._updateDevice.metadata;
    }

    return Object.keys(this._updateDevice).length > 0;
  }

  private _handleSaveSuccess(detail: string): void {
    this._messageService.add({
      severity: this._translateService.instant('common.messages.success.summary'),
      summary: this._translateService.instant('common.messages.success.saved'),
      detail,
    });
  }

  private _handleSaveError(error: unknown): void {
    console.error('Error saving device/images:', error);

    this._messageService.add({
      severity: 'error',
      summary: this._translateService.instant('common.messages.summary.save-failed'),
      detail: this._translateService.instant('common.messages.error.save-failed'),
      life: 5000,
    });
  }
}
